import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trash, CheckCircle, Warning, X, Clock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES = [
  { key: 'deposit',  label: 'Deposit',  color: '#2563eb', prefix: 'D' },
  { key: 'withdraw', label: 'Withdraw', color: '#16a34a', prefix: 'W' },
  { key: 'support',  label: 'Support',  color: '#7c3aed', prefix: 'S' },
];

const RANGE_PRESETS = [
  { key: 'daily',   label: 'Today' },
  { key: 'weekly',  label: 'This Week' },
  { key: 'monthly', label: 'This Month' },
  { key: 'custom',  label: 'Custom' },
];

function isoStart(d) { return new Date(d.setHours(0, 0, 0, 0)).toISOString(); }
function isoEnd(d)   { return new Date(d.setHours(23, 59, 59, 999)).toISOString(); }

function rangeFor(preset, customFrom, customTo) {
  const now = new Date();
  if (preset === 'daily') {
    return { from_iso: isoStart(new Date(now)), to_iso: isoEnd(new Date(now)) };
  }
  if (preset === 'weekly') {
    const start = new Date(now); start.setDate(start.getDate() - 7);
    return { from_iso: isoStart(start), to_iso: isoEnd(new Date(now)) };
  }
  if (preset === 'monthly') {
    const start = new Date(now); start.setMonth(start.getMonth() - 1);
    return { from_iso: isoStart(start), to_iso: isoEnd(new Date(now)) };
  }
  return {
    from_iso: customFrom ? isoStart(new Date(customFrom)) : undefined,
    to_iso:   customTo   ? isoEnd(new Date(customTo))     : undefined,
  };
}

const StatusPill = ({ status }) => (
  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
    status === 'closed' ? 'bg-emerald-100 text-emerald-700'
    : status === 'cancelled' ? 'bg-red-100 text-red-700'
    : 'bg-amber-100 text-amber-700'
  }`}>
    {status === 'closed' ? '● Closed' : status === 'cancelled' ? '✕ Cancelled' : '● Open'}
  </span>
);

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const ServiceTimelines = () => {
  const navigate = useNavigate();
  // Session-47: role-aware chat/settings routes so handlers don't get bounced
  // into /admin-chat (which redirects them out to /handler-dashboard).
  // `user` is read from localStorage lower down via useMemo — reuse that below.
  // Session-41 back-nav fix: persist tab / filters / preset in sessionStorage
  // so returning from an in-request chat lands the user on the exact same
  // view (Deposit tab, Cancelled filter, weekly range, etc.) they left from.
  const _readSS = (k, def) => {
    try { const v = sessionStorage.getItem(`svc_tl_${k}`); return v == null ? def : v; } catch { return def; }
  };
  const [activeType, setActiveType] = useState(() => _readSS('activeType', 'deposit'));
  const [preset, setPreset] = useState(() => _readSS('preset', 'monthly'));
  const [customFrom, setCustomFrom] = useState(() => _readSS('customFrom', ''));
  const [customTo, setCustomTo] = useState(() => _readSS('customTo', ''));
  const [statusFilter, setStatusFilter] = useState(() => _readSS('statusFilter', '')); // '' = all, 'open', 'closed', 'cancelled'
  useEffect(() => { try { sessionStorage.setItem('svc_tl_activeType', activeType); } catch {} }, [activeType]);
  useEffect(() => { try { sessionStorage.setItem('svc_tl_preset', preset); } catch {} }, [preset]);
  useEffect(() => { try { sessionStorage.setItem('svc_tl_customFrom', customFrom); } catch {} }, [customFrom]);
  useEffect(() => { try { sessionStorage.setItem('svc_tl_customTo', customTo); } catch {} }, [customTo]);
  useEffect(() => { try { sessionStorage.setItem('svc_tl_statusFilter', statusFilter); } catch {} }, [statusFilter]);
  const [requests, setRequests] = useState([]);
  const [aggregate, setAggregate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailReq, setDetailReq] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pwdModal, setPwdModal] = useState(null); // { action: 'delete'|'close', ids?, all? }
  const [pwdValue, setPwdValue] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('dws_user') || 'null'); }
    catch { return null; }
  }, []);

  // Session-47: role-aware routes (must come AFTER user is read)
  const isHandler = String(user?.role || '').toLowerCase().endsWith('_handler');
  const chatRouteBase = isHandler ? '/handler-chat' : '/admin-chat';
  const settingsRoute = isHandler ? '/handler-settings' : '/admin-settings';
  const timelinesRoute = isHandler ? '/handler/service-timelines' : '/admin/service-timelines';

  const range = useMemo(
    () => rangeFor(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: activeType, limit: 500, ...range };
      if (statusFilter) params.status = statusFilter;
      const [listRes, aggRes] = await Promise.all([
        axios.get(`${API}/service-requests`, { params }),
        axios.get(`${API}/service-requests/aggregate`, { params: { type: activeType, ...range } }),
      ]);
      setRequests(listRes.data.items || []);
      setAggregate(aggRes.data.items || []);
      setSelectedIds([]);
    } catch (e) {
      console.error('Service Timelines fetch failed', e);
    } finally { setLoading(false); }
  }, [activeType, range, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAll = () => setSelectedIds(requests.map(r => r.id));
  const clearSel = () => setSelectedIds([]);

  const openChat = (req) => {
    if (!req?.room_id || !req?.customer_id) return;
    // Session-41 back-nav fix: pass `from` so AdminChat's back button lands
    // right back on Service Timelines (with tab + filter restored via
    // sessionStorage) instead of dumping the user out to /admin-dashboard.
    navigate(`${chatRouteBase}/${req.room_id}`, { state: { from: timelinesRoute } });
  };

  const submitPwd = async () => {
    if (!pwdModal || !pwdValue) return;
    setPwdBusy(true);
    try {
      const payload = {
        admin_id: user?.id,
        password: pwdValue,
        type: activeType,
        ids: pwdModal.ids && pwdModal.ids.length ? pwdModal.ids : undefined,
        from_iso: pwdModal.useRange ? range.from_iso : undefined,
        to_iso: pwdModal.useRange ? range.to_iso : undefined,
      };
      const url = pwdModal.action === 'delete'
        ? `${API}/service-requests/bulk-delete`
        : `${API}/service-requests/bulk-close`;
      await axios.post(url, payload);
      setPwdModal(null); setPwdValue('');
      await fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally { setPwdBusy(false); }
  };

  const activeMeta = TYPES.find(t => t.key === activeType) || TYPES[0];

  return (
    <div className="fixed inset-0 bg-slate-50 flex flex-col overflow-hidden">
      <div className="flex-shrink-0">
      {/* Header */}
      <div className="sticky top-0 bg-[#008069] text-white px-4 py-3 flex items-center gap-3 shadow-md z-10">
        {/* Session-43 back-nav fix: always land back on Admin Settings —
            `navigate(-1)` was pulling the user back into a previously-opened
            chat when timelines was reached from within one. */}
        <button onClick={() => navigate(settingsRoute)} className="p-1 hover:bg-white/10 rounded" data-testid="sr-back-btn">
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-lg leading-tight">Service Timelines</h1>
          <p className="text-[11px] opacity-80">Deposit · Withdraw · Support requests</p>
        </div>
      </div>

      {/* Type tabs */}
      <div className="bg-white border-b border-slate-200 flex overflow-x-auto">
        {TYPES.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveType(t.key)}
            className={`flex-1 min-w-[110px] px-3 py-3 text-sm font-semibold border-b-2 transition ${
              activeType === t.key ? 'text-slate-800' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
            style={activeType === t.key ? { borderColor: t.color } : {}}
            data-testid={`sr-type-${t.key}`}
          >
            {t.label} Requests
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex flex-wrap items-center gap-2">
        {RANGE_PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            className={`text-[12px] px-2.5 py-1 rounded-full border ${
              preset === p.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
            }`}
            data-testid={`sr-range-${p.key}`}
          >{p.label}</button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-1">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                   className="text-[12px] px-2 py-1 border border-slate-300 rounded" data-testid="sr-custom-from" />
            <span className="text-[11px] text-slate-500">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                   className="text-[12px] px-2 py-1 border border-slate-300 rounded" data-testid="sr-custom-to" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={fetchAll}
                  className="text-[12px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                  data-testid="sr-refresh">Refresh</button>
        </div>
      </div>

      {/* Session-40: status filter chips */}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase font-bold text-slate-400 mr-1">Status:</span>
        {[
          { key: '',          label: 'All' },
          { key: 'open',      label: 'Open' },
          { key: 'closed',    label: 'Closed' },
          { key: 'cancelled', label: 'Cancelled' },
        ].map(s => (
          <button key={s.key || 'all'}
                  onClick={() => setStatusFilter(s.key)}
                  className={`text-[12px] px-2.5 py-1 rounded-full border ${
                    statusFilter === s.key
                      ? (s.key === 'cancelled' ? 'bg-red-600 text-white border-red-600'
                         : s.key === 'closed' ? 'bg-emerald-600 text-white border-emerald-600'
                         : s.key === 'open' ? 'bg-amber-600 text-white border-amber-600'
                         : 'bg-slate-800 text-white border-slate-800')
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
                  }`}
                  data-testid={`sr-status-${s.key || 'all'}`}>{s.label}</button>
        ))}
      </div>
      </div>

      {/* Aggregate cards */}
      <div className="flex-1 overflow-y-auto p-3">
        <h2 className="text-[11px] uppercase font-bold text-slate-500 mb-2 tracking-wide">Client-wise Summary</h2>
        {aggregate.length === 0 ? (
          <div className="text-[12px] text-slate-400 py-2">No data in this range.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {aggregate.slice(0, 20).map(row => (
              <div key={row.customer_id} className="bg-white border border-slate-200 rounded-lg p-2.5 flex items-center gap-3"
                   data-testid={`sr-agg-${row.customer_id}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                     style={{ backgroundColor: activeMeta.color }}>
                  {(row.customer_name || 'C').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">{row.customer_name || 'Unknown'}</div>
                  <div className="text-[10px] text-slate-500 flex gap-2">
                    <span>Total: <b className="text-slate-700">{row.total}</b></span>
                    <span className="text-amber-600">Open: <b>{row.open}</b></span>
                    <span className="text-emerald-600">Closed: <b>{row.closed}</b></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions row */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <h2 className="text-[11px] uppercase font-bold text-slate-500 tracking-wide flex-1">
            Individual Requests ({requests.length})
          </h2>
          <button onClick={selectAll}
                  className="text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                  data-testid="sr-select-all">Select All</button>
          <button onClick={clearSel} disabled={selectedIds.length === 0}
                  className="text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-40"
                  data-testid="sr-clear-sel">Clear ({selectedIds.length})</button>
          <button onClick={() => setPwdModal({ action: 'close', ids: selectedIds.length ? selectedIds : null, useRange: !selectedIds.length })}
                  className="text-[11px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                  data-testid="sr-bulk-close-btn">
            <CheckCircle size={13} /> Close {selectedIds.length ? `(${selectedIds.length})` : 'range'}
          </button>
          <button onClick={() => setPwdModal({ action: 'delete', ids: selectedIds.length ? selectedIds : null, useRange: !selectedIds.length })}
                  className="text-[11px] px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white flex items-center gap-1"
                  data-testid="sr-bulk-delete-btn">
            <Trash size={13} /> Delete {selectedIds.length ? `(${selectedIds.length})` : 'range'}
          </button>
        </div>

        {/* Requests table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-6 text-center text-[12px] text-slate-400">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="p-6 text-center text-[12px] text-slate-400">No requests in this range.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {requests.map(req => (
                <div key={req.id}
                     className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50"
                     data-testid={`sr-row-${req.request_no}`}>
                  <input type="checkbox" checked={selectedIds.includes(req.id)}
                         onChange={() => toggleSelect(req.id)}
                         className="w-4 h-4" />
                  <button onClick={() => setDetailReq(req)}
                          className="font-bold text-[13px] text-slate-800 hover:text-blue-600 min-w-[52px] text-left">
                    {req.request_no}
                  </button>
                  <button onClick={() => openChat(req)}
                          className="text-[13px] text-blue-600 hover:underline flex-1 min-w-0 truncate text-left"
                          title="Open chat where request started">
                    {req.customer_name || 'Unknown'}
                  </button>
                  <StatusPill status={req.status} />
                  <span className="text-[10px] text-slate-500 whitespace-nowrap">
                    TB: {(() => {
                      // Session-43: compute TB inline for older cancelled rows
                      // that were persisted before the sr_cancel TB fix.
                      if (req.time_between_minutes != null) return `${req.time_between_minutes} min`;
                      const end = req.status === 'cancelled' ? req.cancelled_at : (req.status === 'closed' ? req.closed_at : null);
                      if (end && req.started_at) {
                        try {
                          const dm = Math.max(0, Math.floor((new Date(end) - new Date(req.started_at)) / 60000));
                          return `${dm} min`;
                        } catch { /* silent */ }
                      }
                      return '—';
                    })()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {detailReq && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-2"
             onClick={() => setDetailReq(null)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-4"
               data-testid="sr-detail-modal">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-bold text-lg">🎫 {detailReq.request_no}</div>
              <StatusPill status={detailReq.status} />
              <button onClick={() => setDetailReq(null)} className="ml-auto p-1 hover:bg-slate-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between border-b py-1.5">
                <span className="text-slate-500">Customer</span>
                <button onClick={() => { setDetailReq(null); openChat(detailReq); }}
                        className="text-blue-600 hover:underline font-medium">
                  {detailReq.customer_name || '—'} →
                </button>
              </div>
              <div className="flex justify-between border-b py-1.5">
                <span className="text-slate-500">Type</span>
                <span className="font-medium capitalize">{detailReq.type}</span>
              </div>
              <div className="flex justify-between border-b py-1.5">
                <span className="text-slate-500 flex items-center gap-1"><Clock size={12} /> Started</span>
                <span className="font-medium">{fmtDT(detailReq.started_at)}</span>
              </div>
              <div className="flex justify-between border-b py-1.5">
                <span className="text-slate-500 flex items-center gap-1"><Clock size={12} /> Accepted</span>
                <span className="font-medium">{fmtDT(detailReq.accepted_at)}</span>
              </div>
              <div className="flex justify-between border-b py-1.5">
                <span className="text-slate-500 flex items-center gap-1"><Clock size={12} /> Closed</span>
                <span className="font-medium">{fmtDT(detailReq.closed_at)}</span>
              </div>
              <div className="flex justify-between py-1.5 bg-slate-50 rounded px-2 mt-2">
                <span className="text-slate-600 font-semibold">TB (Total Time)</span>
                <span className="font-bold text-slate-800">
                  {(() => {
                    // Session-43: same inline TB fallback for the detail modal.
                    if (detailReq.time_between_minutes != null) return `${detailReq.time_between_minutes} min`;
                    const end = detailReq.status === 'cancelled' ? detailReq.cancelled_at : (detailReq.status === 'closed' ? detailReq.closed_at : null);
                    if (end && detailReq.started_at) {
                      try {
                        const dm = Math.max(0, Math.floor((new Date(end) - new Date(detailReq.started_at)) / 60000));
                        return `${dm} min`;
                      } catch { /* silent */ }
                    }
                    return '—';
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password confirmation modal */}
      {pwdModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2"
             onClick={() => !pwdBusy && setPwdModal(null)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white rounded-xl w-full max-w-sm p-4"
               data-testid="sr-pwd-modal">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={20} className={pwdModal.action === 'delete' ? 'text-red-600' : 'text-amber-600'} />
              <h3 className="font-bold text-slate-800">
                Confirm {pwdModal.action === 'delete' ? 'Delete' : 'Force-Close'}
              </h3>
            </div>
            <p className="text-[12px] text-slate-600 mb-3">
              {pwdModal.ids && pwdModal.ids.length
                ? `${pwdModal.ids.length} selected requests`
                : `All ${activeType} requests in current filter range`}
              . Enter admin password to confirm.
            </p>
            <input
              type="password" value={pwdValue}
              onChange={e => setPwdValue(e.target.value)}
              placeholder="Admin password"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3"
              autoFocus
              data-testid="sr-pwd-input"
            />
            <div className="flex gap-2 justify-end">
              <button disabled={pwdBusy} onClick={() => { setPwdModal(null); setPwdValue(''); }}
                      className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
              <button disabled={pwdBusy || !pwdValue} onClick={submitPwd}
                      className={`px-3 py-1.5 text-sm rounded text-white ${
                        pwdModal.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                      } disabled:opacity-50`}
                      data-testid="sr-pwd-confirm">
                {pwdBusy ? 'Working…' : (pwdModal.action === 'delete' ? 'Delete' : 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceTimelines;
